/**
 * Sniper — pure, framework-free helpers shared by the Sniper page.
 *
 * The ARM predicate is the FROZEN contract logic (`sniper-contract.md` §counter
 * enum + arm predicate), mirrored EXACTLY from the control-plane
 * `sniper.arm.ts::isArmed` and the Rust `sniper_types.rs::is_armed`:
 *
 *     armed ⇔ count(counter, state) == floor(line)
 *
 * `line` is a half-integer (`X.5`) config-time invariant, so `count ==
 * floor(line)` means the very next increment pushes the total over the line →
 * Over resolves YES. Team-scoped counters (`team_*`) resolve `count` against
 * that team only; with no team a `team_*` count is unresolvable → NOT armed
 * (fail-closed).
 *
 * The `MatchState` these operate on is the BACKEND-AUTHORED state (the
 * `match_state` WS event + `GET /match-events/state/:fixtureId` backfill) — the
 * FE renders it and never re-derives score/corners from raw incidents. `count`
 * and `isArmed` are therefore PURE functions of that backend state, not an
 * interpretation of the incident stream.
 */

import type {
  SniperCounter,
  SniperTeam,
  SniperPeriod,
  MatchState,
  SniperMarketCandidate,
  SniperMarketDraft,
} from '../types/sniper.types';

// ─── wire period ids (Body.Period.Id) ────────────────────────────────────────

/** `MatchState.period` value while the ball is in the FIRST half. */
export const WIRE_PERIOD_FIRST_HALF = 10;
/** `MatchState.period` value while the ball is in the SECOND half. */
export const WIRE_PERIOD_SECOND_HALF = 20;

// ─── arm predicate (contract §counter enum + arm predicate) ──────────────────

/**
 * The counter value for a market in the current state. Returns `null` when the
 * count is unresolvable — a `team_*` counter with no team, or a per-half scope
 * the FE cannot compute (see the `period` param) — fail-closed.
 *
 * `period` narrows the count to a half:
 *   - `full`: the whole-match total (default, unchanged).
 *   - `first_half`: resolvable ONLY while `state.period === WIRE_PERIOD_FIRST_HALF`
 *     — during the 1st half the wire total IS the 1st-half total. Outside period
 *     10 the wire no longer carries a 1H breakdown → `null` (not armed).
 *   - `second_half`: the FE has NO first-half breakdown on the wire, so it cannot
 *     subtract the 1H total to get a 2H count → `null` (fail-closed). Authoritative
 *     2H arming lives in the Rust engine (it keeps per-period scores); the FE
 *     mirror cannot compute it and MUST NOT fabricate a wrong ARMED.
 */
export function count(
  counter: SniperCounter,
  state: MatchState,
  team?: SniperTeam | null,
  period: SniperPeriod = 'full',
): number | null {
  if (period === 'second_half') return null;
  if (period === 'first_half' && state.period !== WIRE_PERIOD_FIRST_HALF) {
    return null;
  }
  switch (counter) {
    case 'match_goals':
      return state.homeScore + state.awayScore;
    case 'match_corners':
      return state.homeCorners + state.awayCorners;
    case 'team_goals':
      if (team === 'home') return state.homeScore;
      if (team === 'away') return state.awayScore;
      return null;
    case 'both_teams_to_score':
      // Match-wide: how many DISTINCT teams have scored (0..2). Half-scoped via
      // the period gate above — during the 1st half the wire totals ARE the 1H
      // totals, so this counts teams that scored in that half.
      return (state.homeScore >= 1 ? 1 : 0) + (state.awayScore >= 1 ? 1 : 0);
    case 'first_team_to_score':
      // The named team's score, but UNRESOLVABLE (`null`) once the OTHER team
      // has scored (first-to-score is lost) or with no team → fail-closed.
      if (team === 'home') return state.awayScore >= 1 ? null : state.homeScore;
      if (team === 'away') return state.homeScore >= 1 ? null : state.awayScore;
      return null;
    case 'team_corners':
      if (team === 'home') return state.homeCorners;
      if (team === 'away') return state.awayCorners;
      return null;
    default:
      return null;
  }
}

/**
 * `armed ⇔ count == floor(line)`. Half-integer `line` is a config-time
 * invariant; this predicate uses `Math.floor(line)` and does not re-validate.
 * A `team_*` counter with no team is never armed. `period` is passed through to
 * `count`, so a `first_half` market only arms during the 1st half and a
 * `second_half` market never arms in the FE mirror (fail-closed — see `count`).
 */
export function isArmed(
  counter: SniperCounter,
  state: MatchState,
  line: number,
  team?: SniperTeam | null,
  period: SniperPeriod = 'full',
): boolean {
  const c = count(counter, state, team, period);
  if (c === null) return false;
  return c === Math.floor(line);
}

// ─── subgroup (trigger-type) ordering ────────────────────────────────────────

/** A market's trigger-type subgroup — the page groups rows under these. */
export type SniperSubgroup = 'goals' | 'corners';

/** Subgroup display order (Gol before Corner) + their Spanish labels. */
export const SNIPER_SUBGROUP_ORDER: SniperSubgroup[] = ['goals', 'corners'];

export const SNIPER_SUBGROUP_LABEL: Record<SniperSubgroup, string> = {
  goals: 'Evento: Gol',
  corners: 'Evento: Corner',
};

/** Map a counter to its trigger-type subgroup. */
export function subgroupForCounter(counter: SniperCounter): SniperSubgroup {
  // BTTS/FTTS are goal-family — their trigger event is a goal.
  return counter === 'match_goals' ||
    counter === 'team_goals' ||
    counter === 'both_teams_to_score' ||
    counter === 'first_team_to_score'
    ? 'goals'
    : 'corners';
}

/**
 * Partition a list of counter-carrying rows into ordered subgroups
 * (goals first, then corners). Empty subgroups are omitted.
 */
export function groupBySubgroup<T extends { counter: SniperCounter }>(
  rows: T[],
): Array<{ subgroup: SniperSubgroup; label: string; rows: T[] }> {
  return SNIPER_SUBGROUP_ORDER.map((subgroup) => ({
    subgroup,
    label: SNIPER_SUBGROUP_LABEL[subgroup],
    rows: rows.filter((r) => subgroupForCounter(r.counter) === subgroup),
  })).filter((g) => g.rows.length > 0);
}

// ─── formatters ──────────────────────────────────────────────────────────────

/** Short half tag for a period — `''` for full-match, `1H`/`2H` for a half. */
export function periodLabel(period: SniperPeriod): string {
  if (period === 'first_half') return '1H';
  if (period === 'second_half') return '2H';
  return '';
}

/**
 * Human label for a counter (venue-agnostic). Optionally suffixes the half tag
 * (e.g. "Match goals · 1H") when a non-full `period` is provided.
 */
export function counterLabel(
  counter: SniperCounter,
  period: SniperPeriod = 'full',
): string {
  const base = (() => {
    switch (counter) {
      case 'match_goals':
        return 'Match goals';
      case 'team_goals':
        return 'Team goals';
      case 'both_teams_to_score':
        return 'Both teams to score';
      case 'first_team_to_score':
        return 'First to score';
      case 'match_corners':
        return 'Match corners';
      case 'team_corners':
        return 'Team corners';
      default:
        return counter;
    }
  })();
  const half = periodLabel(period);
  return half ? `${base} · ${half}` : base;
}

/** Short venue chip label. */
export function venueLabel(venue: string): string {
  if (venue === 'kalshi') return 'Kalshi';
  if (venue === 'polymarket') return 'Polymarket';
  return venue;
}

/** Venue chip accent colour (Kalshi teal, Polymarket indigo). */
export function venueColor(venue: string): string {
  if (venue === 'kalshi') return '#00b8a3';
  if (venue === 'polymarket') return '#5b6ef5';
  return '#9e9e9e';
}

/** `over 2.5 (home)` style summary of a market's target (optionally per-half). */
export function targetSummary(
  counter: SniperCounter,
  line: number,
  team: SniperTeam | null,
  period: SniperPeriod = 'full',
): string {
  // BTTS/FTTS have a FORCED line the operator doesn't own — the "Over 1.5" /
  // "Over 0.5" phrasing reads oddly, so use the counter label alone.
  if (counter === 'both_teams_to_score' || counter === 'first_team_to_score') {
    const base = counterLabel(counter, period);
    return team ? `${base} · ${team}` : base;
  }
  const base = `${counterLabel(counter, period)} Over ${line}`;
  return team ? `${base} · ${team}` : base;
}

/** Dollars → cents string, e.g. 0.99 → "99¢", 0.9 → "90¢". Null → "—". */
export function priceToCents(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return '—';
  return `${Math.round(price * 100)}¢`;
}

/** Confidence-gate 0..1 → integer percent (clamped). */
export function gatePct(gate: number | null | undefined): number {
  if (gate == null || !Number.isFinite(gate)) return 0;
  return Math.max(0, Math.min(100, Math.round(gate * 100)));
}

/**
 * Operator-entered percent (1..100) → wire confidence-gate 0..1. Inverse of
 * `gatePct`. The FE shows/edits the gate as an INTEGER PERCENT while the
 * contract field `confidenceGate` stays 0..1 — so the operator can never again
 * read "Gate 1" as "1%" (it is 1.0 = 100%). Returns `null` for anything that is
 * not a usable gate — non-numeric, ≤ 0, or > 100 — so the caller fails closed
 * (the ✓ stays disabled) instead of writing a bogus gate. A valid percent maps
 * to `pct / 100`, landing in (0, 1].
 */
export function pctToGate(pct: number): number | null {
  if (!Number.isFinite(pct)) return null;
  const rounded = Math.round(pct);
  if (rounded < 1 || rounded > 100) return null;
  return rounded / 100;
}

/**
 * The event's live state line: `{home} h–a {away} · Corners h–a · clock`.
 * Mirrors the Live-match-events header derivation; renders exactly what the
 * feed provides (never a guessed score).
 */
export function formatStateLine(
  state: MatchState,
  homeTeam: string,
  awayTeam: string,
  clockText: string,
): string {
  const score = `${homeTeam} ${state.homeScore}–${state.awayScore} ${awayTeam}`;
  const corners = `Corners ${state.homeCorners}–${state.awayCorners}`;
  return `${score} · ${corners} · ${clockText}`;
}

// ─── line validation (config-time invariant, mirrored client-side) ───────────

/** Half-integer (`X.5`) — the config-time invariant. Integer lines are rejected. */
export function isHalfInteger(line: number): boolean {
  return Number.isFinite(line) && Math.abs(line - Math.floor(line) - 0.5) < 1e-9;
}

// ─── decimal text input normalization (shared by the import dialog) ──────────

/**
 * Normalize an in-progress decimal text field so the operator can freely type
 * `0.5`, `10`, `0,95`, `2.5` (comma = Spanish locale decimal separator). Pure +
 * framework-free so it is unit-testable and reusable by the controlled
 * `DecimalField`:
 *   - comma → dot (`"0,5"` → `"0.5"`),
 *   - strip anything that is not a digit or a dot (`"abc0.5x"` → `"0.5"`),
 *   - keep only the FIRST dot and everything up to the SECOND one, dropping the
 *     rest (`"0.5.3"` → `"0.5"`).
 * An in-progress `"0."` is PRESERVED (not coerced to `"0"`), which is exactly
 * what let the trailing dot survive so `0.5` is reachable.
 */
export function normalizeDecimalInput(raw: string): string {
  const stripped = raw.replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 2) return stripped;
  return `${parts[0]}.${parts[1]}`;
}

// ─── import candidate → editable draft ───────────────────────────────────────

/**
 * Default confidence gate for a new market. Goals arrive at 100% and corners
 * can arrive unconfirmed, so BOTH default to a fire-safe 100% (`1`) — the
 * operator then lowers/raises per market. Kept as a function (not a constant)
 * so a future per-event-type default is a one-line change.
 */
export const DEFAULT_CONFIDENCE_GATE = 1;
export function defaultConfidenceGate(): number {
  return DEFAULT_CONFIDENCE_GATE;
}

/** Sensible default sweep ceiling (worst per-contract price), dollars. */
export const DEFAULT_MAX_PRICE = 0.99;

/** Default limit of the GTC SELL that exits the sweep on a VAR-disallowed goal. */
export const DEFAULT_VAR_EXIT_PRICE = 0.93;

/** Default worst per-contract price for the auto-NO period-close tail sweep. */
export const DEFAULT_NO_MAX_PRICE = 0.99;

// ─── two-phase VAR-defense defaults (mirror of the backend DTO defaults) ─────

/** Master switch for the two-phase VAR defense. Fail-safe ON. */
export const DEFAULT_VAR_DEFENSE_ENABLED = true;
/** Phase 1 — break-even preventive sell at our buy price. Fail-safe ON. */
export const DEFAULT_VAR_PREVENTIVE_EXIT_ENABLED = true;
/**
 * Phase 2 — aggressive-sell floor (dollars) the disallowed-goal position is
 * dumped down to once the annulment is confirmed. A price in (0,1], like
 * `DEFAULT_VAR_EXIT_PRICE`.
 */
export const DEFAULT_VAR_CONFIRMED_FLOOR_PRICE = 0.9;
/** All-clear rebuy if the VAR ultimately confirms the goal. Fail-safe ON. */
export const DEFAULT_VAR_REBUY_ENABLED = true;

/**
 * Counters whose `line` is FORCED by the contract (the operator never sets it),
 * mirroring the backend's `{ both_teams_to_score: 1.5, first_team_to_score: 0.5 }`.
 * BTTS resolves on the 2nd distinct team to score (floor(1.5)=1), FTTS on the
 * first goal of the match/team (floor(0.5)=0).
 */
export const SNIPER_FORCED_LINE: Partial<Record<SniperCounter, number>> = {
  both_teams_to_score: 1.5,
  first_team_to_score: 0.5,
};

/**
 * Build an editable draft from a scraped candidate. A guessed `counter`/`line`
 * becomes an editable default; an UNCLASSIFIED candidate (`null` guess) leaves
 * `counter`/`line` UNSET (`null`) — NEVER silently defaulted to `match_goals`,
 * which would arm/fire the market on goals. Such a draft is INCOMPLETE (see
 * `isDraftComplete`) so the operator must pick a counter before it can be
 * persisted. `enabled` defaults false (fail-safe OFF), `size` defaults 10 (a
 * sensible operator default so a classified total is addable without extra
 * typing — markets are still created DISABLED / fail-safe OFF).
 */
export function candidateToDraft(
  candidate: SniperMarketCandidate,
): SniperMarketDraft {
  // The backend nests the auto-guess under `guess: {counter, team, line}`;
  // older shapes returned them flat. Read from either so the guessed
  // counter/line ALWAYS map (otherwise every row shows unset — the operator
  // would have to re-type the line by hand).
  const g = candidate.guess ?? candidate;
  // `period` lives only on the nested `guess` shape; a flat/legacy candidate has
  // no period → default 'full' (whole match). Corners never carry a half.
  const guessedPeriod =
    'period' in g ? (g as { period?: SniperPeriod | null }).period : null;
  const guessedCounter = g.counter ?? null;
  // BTTS/FTTS carry a FORCED, operator-locked line — it ALWAYS wins over a
  // guessed line so the draft can never carry a stray value the Line field
  // (disabled for these counters) would silently misrepresent.
  const forcedLine = guessedCounter
    ? SNIPER_FORCED_LINE[guessedCounter]
    : undefined;
  return {
    venue: candidate.venue,
    marketId: candidate.marketId,
    marketLabel: candidate.marketLabel,
    counter: guessedCounter,
    team: g.team ?? null,
    period: guessedPeriod ?? 'full',
    line: forcedLine ?? g.line ?? null,
    buySide: 'over',
    size: 10,
    confidenceGate: defaultConfidenceGate(),
    maxPrice: DEFAULT_MAX_PRICE,
    varExitPrice: DEFAULT_VAR_EXIT_PRICE,
    // Auto-NO period-close tail — OFF by default (fail-safe). `noMarketId`
    // prefills from the Polymarket candidate's No token when the scraper
    // carried it; Kalshi candidates have none (native NO) → null.
    autoNoEnabled: true,
    noMaxPrice: DEFAULT_NO_MAX_PRICE,
    noMarketId: candidate.noMarketId ?? null,
    autoNoIncludeExtraTime: false,
    varDefenseEnabled: DEFAULT_VAR_DEFENSE_ENABLED,
    varPreventiveExitEnabled: DEFAULT_VAR_PREVENTIVE_EXIT_ENABLED,
    varConfirmedFloorPrice: DEFAULT_VAR_CONFIRMED_FLOOR_PRICE,
    varRebuyEnabled: DEFAULT_VAR_REBUY_ENABLED,
    enabled: false,
  };
}

/**
 * The first trading-critical field a draft is missing, as a short operator
 * label — or `null` when the draft is complete. Drives the "incomplete · X"
 * chip so the operator sees WHY a row can't be saved (e.g. "set a size", not a
 * silent block on the disabled Team field). Order mirrors `isDraftComplete`.
 */
export function draftIncompleteReason(draft: SniperMarketDraft): string | null {
  if (draft.counter == null) return 'pick a counter';
  if (draft.line == null || !isHalfInteger(draft.line)) {
    return 'invalid line (needs X.5)';
  }
  if (!(draft.size > 0)) return 'set a size';
  // FTTS is team-scoped (REQUIRED); BTTS is match-wide (no team). Both are
  // goal-family so the corners-non-full guard below leaves them alone.
  const teamRequired =
    draft.counter === 'team_goals' ||
    draft.counter === 'team_corners' ||
    draft.counter === 'first_team_to_score';
  if (teamRequired && draft.team == null) return 'pick a team';
  if (!teamRequired && draft.team != null) return 'remove team (match-wide)';
  // Corners are full-match only (no per-half corner feed) — reject a non-full
  // period client-side to mirror the server's 400.
  const isCorners =
    draft.counter === 'match_corners' || draft.counter === 'team_corners';
  if (isCorners && draft.period !== 'full') return 'corners are full-match only';
  if (!(draft.maxPrice > 0) || draft.maxPrice > 1) return 'max price 0–1';
  if (!(draft.varExitPrice > 0) || draft.varExitPrice > 1) {
    return 'exit price 0–1';
  }
  // Phase-2 floor is a price like varExitPrice: mirror the server's (0,1] rule so
  // a batch is never posted with a row the server would reject.
  if (!(draft.varConfirmedFloorPrice > 0) || draft.varConfirmedFloorPrice > 1) {
    return 'floor price 0–1';
  }
  if (!(draft.confidenceGate >= 0 && draft.confidenceGate <= 1)) {
    return 'confidence 0–100%';
  }
  // Auto-NO period-close tail (only validated when enabled). A Polymarket tail
  // needs its own No/Under tokenId (no native NO); Kalshi does NOT (native NO).
  // The noMaxPrice is the sweep ceiling and must be in (0,1] like maxPrice.
  if (draft.autoNoEnabled) {
    if (draft.venue === 'polymarket' && !draft.noMarketId) {
      return 'auto-NO needs a Polymarket No tokenId';
    }
    // NaN-safe, mirroring maxPrice/varExitPrice: `!(x > 0)` rejects NaN too.
    if (draft.noMaxPrice == null || !(draft.noMaxPrice > 0) || draft.noMaxPrice > 1) {
      return 'auto-NO needs a valid noMaxPrice (0,1]';
    }
  }
  return null;
}

/**
 * A draft is persistable only when every trading-critical field resolves:
 * an explicit counter, a half-integer line, a positive size, a team iff the
 * counter is `team_*`, and a 0..1 gate + maxPrice/varExitPrice/varConfirmedFloorPrice
 * in (0,1].
 * Mirrors the server-side validation so the operator can't POST a guess that
 * would be rejected. Single source of truth: `draftIncompleteReason`.
 */
export function isDraftComplete(draft: SniperMarketDraft): boolean {
  return draftIncompleteReason(draft) === null;
}
